import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useState } from 'react';

import { ERROR_ID } from '../api/errors';
import { useGlobalError } from '../context/GlobalErrorContext';

/**
 * Response-less failures are usually transient (iOS Safari reconnecting after waking from
 * background) and resolve via the query's own retries, so they only surface after this grace.
 */
const NETWORK_ERROR_GRACE_MS = 4_000;

function createQueryClient(setError) {
  let pendingNetworkError;

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isAxiosError(error) && error.response) {
          if (!query.meta?.handlesResponseError) {
            setError(error);
          }
          return;
        }
        pendingNetworkError ??= setTimeout(() => {
          pendingNetworkError = undefined;
          setError({ errorId: ERROR_ID.NETWORK_ERROR, error });
        }, NETWORK_ERROR_GRACE_MS);
      },
      onSuccess: () => {
        clearTimeout(pendingNetworkError);
        pendingNetworkError = undefined;
      },
    }),
  });
}

export function QueryProvider({ children }) {
  const { setError } = useGlobalError();
  const [queryClient] = useState(() => createQueryClient(setError));

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
